import {
  BlogPost,
  HomepageSettings,
  PostsResponse,
  ApiError,
  UploadResponse,
  FileUploadData,
} from "@/types/api";
import { DEFAULT_HOMEPAGE_SETTINGS } from "@/lib/config";
import { AuthService } from "@/lib/auth";
import { apiLogger } from "@/services/logger";

/**
 * Centralized API service - eliminates 300+ lines of duplicate fetch logic
 * Provides consistent error handling, typing, caching, and request deduplication
 */
class ApiService {
  private baseUrl = "";
  private cache = new Map<string, { data: unknown; timestamp: number }>();
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  // Request deduplication - prevents multiple identical requests
  private inflightRequests = new Map<string, Promise<unknown>>();

  /**
   * Generic fetch with error handling, type safety, and retry logic
   */
  private async fetchWithErrorHandling<T>(
    url: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const maxRetries = 3;
    const retryDelay = (attempt: number) => Math.min(1000 * Math.pow(2, attempt), 4000); // 1s, 2s, 4s

    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const status = response.status;

        // Retry on 503 (Service Unavailable), 504 (Gateway Timeout), or 429 (Rate Limit)
        const shouldRetry =
          (status === 503 || status === 504 || status === 429) &&
          retryCount < maxRetries;

        if (shouldRetry) {
          const delay = retryDelay(retryCount);
          apiLogger.warn(`HTTP ${status} - Retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`, {
            action: "retry",
            metadata: {
              url,
              status,
              attempt: retryCount + 1,
              maxRetries,
              delay,
            },
          });

          await new Promise(resolve => setTimeout(resolve, delay));
          return this.fetchWithErrorHandling<T>(url, options, retryCount + 1);
        }

        throw new ApiError(
          `HTTP error! status: ${response.status}`,
          response.status
        );
      }

      // Log successful retry
      if (retryCount > 0) {
        apiLogger.info(`Request succeeded after ${retryCount} ${retryCount === 1 ? 'retry' : 'retries'}`, {
          action: "retry_success",
          metadata: {
            url,
            retries: retryCount,
          },
        });
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // Handle network errors (including timeouts)
      const isNetworkError =
        error instanceof Error &&
        (error.name === 'AbortError' ||
         error.message.includes('fetch') ||
         error.message.includes('timeout'));

      const shouldRetryNetwork = isNetworkError && retryCount < maxRetries;

      if (shouldRetryNetwork) {
        const delay = retryDelay(retryCount);
        apiLogger.warn(`Network error - Retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`, {
          action: "retry_network",
          metadata: {
            url,
            error: error instanceof Error ? error.message : String(error),
            attempt: retryCount + 1,
            maxRetries,
            delay,
          },
        });

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.fetchWithErrorHandling<T>(url, options, retryCount + 1);
      }

      apiLogger.error("API request failed", {
        action: "request",
        metadata: {
          url,
          retries: retryCount,
        }
      }, error);

      throw new ApiError(
        error instanceof Error ? error.message : "Unknown error occurred",
        0
      );
    }
  }

  /**
   * Cache management
   */
  private getCached<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data as T;
    }
    return null;
  }

  private setCache(key: string, data: unknown): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Request deduplication - ensures only one request per key is in flight
   */
  private async executeWithDeduplication<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // Check if there's already an in-flight request for this key
    const existingRequest = this.inflightRequests.get(key) as Promise<T>;
    if (existingRequest) {
      apiLogger.debug(`Deduplicating request: ${key}`, { action: "deduplication" });
      return existingRequest;
    }

    // Create new request and track it
    const request = requestFn();
    this.inflightRequests.set(key, request);

    try {
      const result = await request;
      return result;
    } finally {
      // Clean up completed request
      this.inflightRequests.delete(key);
    }
  }

  /**
   * Fetch homepage settings with caching and deduplication
   */
  async getHomepageSettings(useCache = true): Promise<HomepageSettings> {
    const cacheKey = "homepage-settings";
    const dedupeKey = `homepage-settings-${useCache}`;

    if (useCache) {
      const cached = this.getCached<HomepageSettings>(cacheKey);
      if (cached) {
        apiLogger.debug("Using cached homepage settings", { action: "cache_hit" });
        return cached;
      }
    }

    return this.executeWithDeduplication(dedupeKey, async () => {
      apiLogger.info("Fetching homepage settings from API", { action: "fetch_homepage" });
      try {
        const response = await this.fetchWithErrorHandling<any>(
          "/api/homepage-settings"
        );

        // Handle different response structures
        const settings = response.data?.settings || response.settings;

        this.setCache(cacheKey, settings);
        apiLogger.info('Homepage settings fetched and cached', {
          action: 'homepage_settings_cached'
        });
        return settings;
      } catch (error) {
        apiLogger.error('Failed to fetch homepage settings, using defaults', {
          action: 'homepage_settings_fetch_failed'
        }, error);

        // Return default settings if API fails
        const defaultSettings = DEFAULT_HOMEPAGE_SETTINGS;
        this.setCache(cacheKey, defaultSettings);
        return defaultSettings;
      }
    });
  }

  /**
   * Fetch posts by category with caching and deduplication
   */
  async getPostsByCategory(
    category: BlogPost["category"],
    useCache = true
  ): Promise<BlogPost[]> {
    const cacheKey = `posts-${category}`;
    const dedupeKey = `posts-${category}-${useCache}`;

    if (useCache) {
      const cached = this.getCached<BlogPost[]>(cacheKey);
      if (cached) {
        apiLogger.debug('Using cached posts', {
          action: 'posts_cache_hit',
          metadata: {
            category
          }
        });
        return cached;
      }
    }

    return this.executeWithDeduplication(dedupeKey, async () => {
      apiLogger.info('Fetching posts from API', {
        action: 'posts_fetch_start',
        metadata: {
          category
        }
      });
      try {
        const response = await this.fetchWithErrorHandling<PostsResponse>(
          `/api/posts?category=${category}`
        );
        const posts = response.posts;

        this.setCache(cacheKey, posts);
        apiLogger.info('Posts fetched and cached', {
          action: 'posts_cached',
          metadata: {
            category,
            postsCount: posts.length
          }
        });
        return posts;
      } catch (error) {
        apiLogger.error('Failed to fetch posts', {
          action: 'posts_fetch_failed',
          metadata: {
            category
          }
        }, error);
        throw new ApiError(`Failed to load ${category} posts`, 0);
      }
    });
  }

  /**
   * Get posts with featured ordering for UX case studies
   */
  async getUXCaseStudiesWithFeatured(): Promise<{
    posts: BlogPost[];
    settings: HomepageSettings["uxCaseStudies"];
  }> {
    try {
      const [settings, allPosts] = await Promise.all([
        this.getHomepageSettings(),
        this.getPostsByCategory("ux-case-studies"),
      ]);

      const uxSettings =
        settings?.uxCaseStudies || DEFAULT_HOMEPAGE_SETTINGS.uxCaseStudies;

      // Order featured posts according to settings order
      let displayPosts = allPosts;
      if (uxSettings.featuredPosts && uxSettings.featuredPosts.length > 0) {
        const featuredPosts = uxSettings.featuredPosts
          .map((slug) => allPosts.find((post) => post.slug === slug))
          .filter(Boolean) as BlogPost[];

        const nonFeaturedPosts = allPosts.filter(
          (post) => !uxSettings.featuredPosts.includes(post.slug)
        );

        displayPosts = [...featuredPosts, ...nonFeaturedPosts];
      }

      return {
        posts: displayPosts.slice(0, uxSettings.maxItems || 4),
        settings: uxSettings,
      };
    } catch (error) {
      apiLogger.error('Failed to fetch UX case studies with featured ordering', {
        action: 'ux_featured_fetch_failed'
      }, error);
      throw error;
    }
  }

  /**
   * Get digital marketing posts with category mapping
   */
  async getDigitalMarketingWithCategories(): Promise<{
    posts: { [key: string]: BlogPost | null };
    settings: HomepageSettings["digitalMarketing"];
  }> {
    try {
      const [settings, allPosts] = await Promise.all([
        this.getHomepageSettings(),
        this.getPostsByCategory("digital-marketing"),
      ]);

      const digitalMarketingSettings =
        settings?.digitalMarketing ||
        DEFAULT_HOMEPAGE_SETTINGS.digitalMarketing;

      // No longer using categories - using tag-based filtering instead
      return {
        posts: {},
        settings: digitalMarketingSettings,
      };
    } catch (error) {
      apiLogger.error('Failed to fetch digital marketing with categories', {
        action: 'digital_marketing_fetch_failed'
      }, error);
      throw error;
    }
  }

  /**
   * Get single post by slug and category
   */
  async getPost(
    slug: string,
    category: BlogPost["category"]
  ): Promise<BlogPost> {
    try {
      const response = await this.fetchWithErrorHandling<{ post: BlogPost }>(
        `/api/posts/${slug}?category=${category}`
      );
      return response.post;
    } catch (error) {
      apiLogger.error('Failed to fetch post', {
        action: 'post_fetch_failed',
        metadata: {
          slug
        }
      }, error);
      throw new ApiError("Failed to load post", 0);
    }
  }

  /**
   * Delete post by slug and category
   */
  async deletePost(
    slug: string,
    category: BlogPost["category"]
  ): Promise<void> {
    try {
      const token = await AuthService.getAccessToken();
      await this.fetchWithErrorHandling(
        `/api/posts/${slug}?category=${category}`,
        {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Invalidate related caches to prevent deleted posts from reappearing
      apiLogger.info('Invalidating post caches after deletion', {
        action: 'cache_invalidate_after_delete'
      });
      this.cache.delete(`posts-${category}`);
      this.cache.delete("posts-ux-case-studies"); // Clear both categories just in case
      this.cache.delete("posts-digital-marketing");
      this.cache.delete("posts-art-direction");
      this.cache.delete("posts-creative-works");
      this.cache.delete("posts-odds-and-ends");
    } catch (error) {
      apiLogger.error('Failed to delete post', {
        action: 'post_delete_failed',
        metadata: {
          slug
        }
      }, error);
      throw new ApiError("Failed to delete post", 0);
    }
  }

  /**
   * Upload file with proper error handling
   */
  async uploadFile(uploadData: FileUploadData): Promise<UploadResponse> {
    try {
      // Get access token for authenticated upload
      const token = await AuthService.getAccessToken();
      if (!token) {
        throw new ApiError("Authentication required for file upload", 401);
      }

      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("type", uploadData.type);
      formData.append("postId", uploadData.postId.toString());

      // Log postId-based upload
      apiLogger.info('File upload with postId', {
        action: 'upload_postid',
        metadata: {
          postId: uploadData.postId,
          type: uploadData.type,
          filename: uploadData.file.name
        }
      });

      const response = await fetch("/api/upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        // Get more detailed error information for debugging
        let errorDetails = '';
        try {
          const errorBody = await response.text();
          errorDetails = errorBody ? ` - ${errorBody.substring(0, 200)}` : '';
        } catch (e) {
          // Ignore error reading response body
        }
        
        const errorMessage = `Upload failed: ${response.status}${errorDetails}`;
        apiLogger.error('Upload API returned error response', {
          action: 'upload_api_error',
          metadata: {
            status: response.status,
            statusText: response.statusText,
            fileSize: uploadData.file.size,
            fileName: uploadData.file.name,
            fileType: uploadData.file.type,
            errorDetails: errorDetails.substring(0, 100)
          }
        });
        
        throw new ApiError(errorMessage, response.status);
      }

      const result = await response.json();
      apiLogger.debug('Upload successful', {
        action: 'upload_success',
        metadata: {
          fileName: uploadData.file.name,
          fileSize: uploadData.file.size,
          responseUrl: result.url?.substring(0, 50)
        }
      });
      
      return result;
    } catch (error) {
      apiLogger.error('File upload failed', {
        action: 'file_upload_failed'
      }, error);
      throw new ApiError(
        error instanceof Error ? error.message : "Upload failed",
        0
      );
    }
  }

  /**
   * Save homepage settings
   */
  async saveHomepageSettings(settings: HomepageSettings): Promise<void> {
    try {
      const token = await AuthService.getAccessToken();
      await this.fetchWithErrorHandling("/api/homepage-settings", {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify({ settings }),
      });

      // Invalidate cache
      this.cache.delete("homepage-settings");
    } catch (error) {
      apiLogger.error('Failed to save homepage settings', {
        action: 'homepage_settings_save_failed'
      }, error);
      throw new ApiError("Failed to save settings", 0);
    }
  }

  /**
   * Clear cache manually
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get Instagram posts
   */
  async getInstagramPosts(): Promise<{
    posts: Array<{
      id: string;
      caption: string;
      media_url: string;
      media_type: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
      permalink: string;
      timestamp: string;
    }>;
    username?: string;
  }> {
    try {
      const response = await this.fetchWithErrorHandling<{
        posts: Array<{
          id: string;
          caption: string;
          media_url: string;
          media_type: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
          permalink: string;
          timestamp: string;
        }>;
        username?: string;
      }>("/api/instagram/posts");

      return response;
    } catch (error) {
      apiLogger.error('Failed to fetch Instagram posts', {
        action: 'instagram_fetch_failed'
      }, error);
      throw new ApiError("Failed to load Instagram posts", 0);
    }
  }

  /**
   * Get cache and performance statistics (for debugging)
   */
  getCacheStats(): {
    cache: { size: number; keys: string[] };
    inflightRequests: { size: number; keys: string[] };
  } {
    return {
      cache: {
        size: this.cache.size,
        keys: Array.from(this.cache.keys()),
      },
      inflightRequests: {
        size: this.inflightRequests.size,
        keys: Array.from(this.inflightRequests.keys()),
      },
    };
  }

  /**
   * Clear all caches and in-flight requests
   */
  clearAllCaches(): void {
    this.cache.clear();
    this.inflightRequests.clear();
    apiLogger.info('All caches and in-flight requests cleared', {
      action: 'cache_clear_all'
    });
  }
}

// Singleton instance
export const apiService = new ApiService();
export default apiService;
